import * as React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Values = { name: string; description?: string | null };

export function FlowEditSheet(props: {
  open: boolean;
  mode: "create" | "edit";
  initial?: Partial<Values>;
  onOpenChange: (v: boolean) => void;
  onSubmit: (v: Values) => Promise<void>;
}) {
  const { open, onOpenChange, mode, initial, onSubmit } = props;
  const [name, setName] = React.useState(initial?.name ?? "");
  const [description, setDescription] = React.useState<string>(initial?.description ?? "");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setName(initial?.name ?? "");
      setDescription((initial?.description as any) ?? "");
    }
  }, [open, initial?.name, initial?.description]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      setError(null);
      await onSubmit({ name: name.trim(), description: (description?.trim?.() || null) as any });
      onOpenChange(false);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[420px]">
        <SheetHeader>
          <SheetTitle>{mode === "create" ? "New flow" : "Edit flow"}</SheetTitle>
        </SheetHeader>
        <form onSubmit={submit} className="mt-4 space-y-3" aria-busy={submitting}>
          <div className="grid gap-1">
            <Label htmlFor="flow-name">Name</Label>
            <Input id="flow-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus required disabled={submitting} />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="flow-desc">Description</Label>
            <textarea
              id="flow-desc"
              className="min-h-[96px] w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={description ?? ""}
              onChange={(e) => setDescription(e.target.value)}
              disabled={submitting}
              placeholder="Optional"
            />
          </div>
          {error && (
            <div className="text-sm text-destructive" role="alert">{error}</div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting || !name.trim()} aria-busy={submitting}>
              {submitting ? (mode === "create" ? "Creating…" : "Saving…") : (mode === "create" ? "Create" : "Save")}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

export default FlowEditSheet;
